import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Sparkles, Mail, MessageSquare, Send, Loader2, CheckCircle,
  HelpCircle, Bug, Lightbulb, DollarSign, Shield, Users
} from 'lucide-react';

const INQUIRY_TYPES = [
  { value: 'general', label: 'General Inquiry', icon: HelpCircle },
  { value: 'support', label: 'Technical Support', icon: Bug },
  { value: 'feedback', label: 'Feedback / Suggestion', icon: Lightbulb },
  { value: 'billing', label: 'Billing / Payments', icon: DollarSign },
  { value: 'safety', label: 'Safety / Report Issue', icon: Shield },
  { value: 'partnership', label: 'Partnership / Business', icon: Users },
];

const ContactUs = () => {
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    inquiryType: 'general',
    subject: '',
    message: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await api.post('/contact', {
        name: formData.name,
        email: formData.email,
        inquiry_type: formData.inquiryType,
        subject: formData.subject,
        message: formData.message,
        user_id: user?.user_id || null,
      });
      setSubmitted(true);
      toast.success('Message sent! We\'ll get back to you soon.');
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-24 pb-12 px-6 hero-bg">
        <div className="hero-glow absolute inset-0" />
        <div className="max-w-xl mx-auto relative z-10">
          <Card className="border-border/50">
            <CardContent className="pt-12 pb-8 text-center space-y-6">
              <div className="mx-auto w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
                Message Sent!
              </h1>
              <p className="text-muted-foreground">
                Thank you for reaching out. Our team will review your message and get back to you within 24-48 hours.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                <Button onClick={() => setSubmitted(false)} variant="outline" className="rounded-xl">
                  Send Another Message
                </Button>
                <Link to="/">
                  <Button className="rounded-xl btn-glow w-full sm:w-auto">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12 px-6 hero-bg">
      <div className="hero-glow absolute inset-0" />
      
      <div className="max-w-4xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <Sparkles className="h-8 w-8 text-primary" />
            <span className="text-2xl font-bold" style={{ fontFamily: 'Syne, sans-serif' }}>
              Bookd
            </span>
          </Link>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
            Contact Us
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Have a question, feedback, or need help? We'd love to hear from you. 
            Our team typically responds within 24-48 hours.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Contact Form */}
          <div className="md:col-span-2">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Send us a Message
                </CardTitle>
                <CardDescription>
                  Fill out the form below and we'll get back to you as soon as possible.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Your name"
                        className="rounded-xl"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="you@example.com"
                        className="rounded-xl"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Inquiry Type</Label>
                    <Select
                      value={formData.inquiryType}
                      onValueChange={(value) => setFormData({ ...formData, inquiryType: value })}
                    >
                      <SelectTrigger className="rounded-xl" data-testid="contact-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {INQUIRY_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <type.icon className="h-4 w-4" />
                              {type.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      placeholder="Brief description of your inquiry"
                      className="rounded-xl"
                      data-testid="contact-subject-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      placeholder="Please provide as much detail as possible..."
                      className="rounded-xl min-h-[150px]"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 rounded-xl btn-glow"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info Sidebar */}
          <div className="space-y-6">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="text-lg">Quick Help</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-medium mb-1">Email Us</h4>
                  <a 
                    href="mailto:support@alwaysbookd.com" 
                    className="text-sm text-primary hover:underline"
                  >
                    support@alwaysbookd.com
                  </a>
                </div>
                <div>
                  <h4 className="font-medium mb-1">Response Time</h4>
                  <p className="text-sm text-muted-foreground">
                    We typically respond within 24-48 business hours.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="text-lg">Common Topics</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link to="/terms" className="text-muted-foreground hover:text-foreground hover:underline">
                      Terms of Service
                    </Link>
                  </li>
                  <li>
                    <Link to="/privacy" className="text-muted-foreground hover:text-foreground hover:underline">
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link to="/cancellation-policy" className="text-muted-foreground hover:text-foreground hover:underline">
                      Cancellation Policy
                    </Link>
                  </li>
                  <li>
                    <Link to="/subscriptions" className="text-muted-foreground hover:text-foreground hover:underline">
                      Subscription Plans
                    </Link>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {!isAuthenticated && (
              <Card className="border-primary/30 bg-primary/5">
                <CardContent className="pt-6">
                  <p className="text-sm text-muted-foreground mb-3">
                    Already have an account? Log in for faster support.
                  </p>
                  <Link to="/login">
                    <Button variant="outline" size="sm" className="w-full rounded-xl">
                      Log In
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
